/**
 * Recording session management utilities
 * Handles persistence and restoration of recording sessions in IndexedDB
 */
import React from "react";

import { datadogLogs } from "@datadog/browser-logs";

import {
  appInstanceId,
  dispatchTabSyncMessage
} from "hooks/use-sync-between-tabs.hook";
import { viteEnv } from "utils/vite-env.util";

import {
  DATABASE_STORES,
  RECORDING_SESSION_ID,
  RECORDING_STATE_PAUSED,
  TAB_SYNC_ACTIONS
} from "./medical-scribe-constants";
import type { RecordingSession } from "./medical-scribe-db/index-db-types.type";
import { getDB } from "./medical-scribe-db/medical-scribe-db.util";
import {
  pauseScribeRecording,
  startScribeRecording
} from "./medical-scribe.slice";
import { SESSION_TIMEOUT_MS } from "./utils";

const isLocalEnv = viteEnv.VITE_ENV === "local";

/**
 * Save a recording session to IndexedDB
 */
export const saveRecordingSession = async (
  session: Omit<RecordingSession, "id" | "lastActiveTime">
) => {
  try {
    const db = await getDB();
    const sessionData: RecordingSession = {
      ...session,
      id: RECORDING_SESSION_ID,
      lastActiveTime: Date.now(),
      tabInstanceId: session.tabInstanceId || appInstanceId // Ensure tabInstanceId is always set
    };
    await db.put(DATABASE_STORES.RECORDING_SESSIONS, sessionData);
    // Note: This is called before recording starts, so recordingStartTimeRef is not available
    if (isLocalEnv) {
      console.log(
        "Recording session saved to IndexedDB with tabInstanceId:",
        sessionData.tabInstanceId
      );
    } else {
      datadogLogs.logger.log("Ambient Scribe", {
        message: "Recording session saved to IndexedDB",
        tabInstanceId: sessionData.tabInstanceId
      });
    }
  } catch (error) {
    if (isLocalEnv) {
      console.error("Failed to save recording session:", error);
    } else {
      datadogLogs.logger.error(
        "Ambient Scribe",
        {
          message: "Failed to save recording session",
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
                }
              : error
        },
        error instanceof Error ? error : undefined
      );
    }
  }
};

/**
 * Get the current recording session from IndexedDB
 * Returns null if session doesn't exist or is stale
 */
export const getRecordingSession =
  async (): Promise<RecordingSession | null> => {
    try {
      const db = await getDB();
      const session = await db.get(
        DATABASE_STORES.RECORDING_SESSIONS,
        RECORDING_SESSION_ID
      );
      if (!session) return null;

      // Check if session is stale (older than SESSION_TIMEOUT_MS)
      const sessionAge = Date.now() - session.lastActiveTime;
      if (sessionAge > SESSION_TIMEOUT_MS) {
        if (isLocalEnv) {
          console.log("Found stale recording session, removing it");
        } else {
          datadogLogs.logger.log("Ambient Scribe", {
            message: "Found stale recording session, removing it",
            sessionAge
          });
        }
        await clearRecordingSession();
        return null;
      }

      return session;
    } catch (error) {
      if (isLocalEnv) {
        console.error("Failed to get recording session:", error);
      } else {
        datadogLogs.logger.error(
          "Ambient Scribe",
          {
            message: "Failed to get recording session",
            error:
              error instanceof Error
                ? {
                    name: error.name,
                    message: error.message,
                    stack: error.stack
                  }
                : error
          },
          error instanceof Error ? error : undefined
        );
      }
      return null;
    }
  };

/**
 * Clear the recording session from IndexedDB
 */
export const clearRecordingSession = async () => {
  try {
    const db = await getDB();
    await db.delete(DATABASE_STORES.RECORDING_SESSIONS, RECORDING_SESSION_ID);
    if (isLocalEnv) {
      console.log("Recording session cleared from IndexedDB");
    } else {
      datadogLogs.logger.log("Ambient Scribe", {
        message: "Recording session cleared from IndexedDB"
      });
    }
  } catch (error) {
    if (isLocalEnv) {
      console.error("Failed to clear recording session:", error);
    } else {
      datadogLogs.logger.error(
        "Ambient Scribe",
        {
          message: "Failed to clear recording session",
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
                }
              : error
        },
        error instanceof Error ? error : undefined
      );
    }
  }
};

/**
 * Update the recording session state in IndexedDB
 */
export const updateRecordingSessionState = async (
  recordingState: number,
  additionalData?: Partial<RecordingSession>
) => {
  try {
    const db = await getDB();
    const session = await db.get(
      DATABASE_STORES.RECORDING_SESSIONS,
      RECORDING_SESSION_ID
    );
    if (session) {
      session.recordingState = recordingState;
      session.lastActiveTime = Date.now();

      // Ensure tabInstanceId is preserved or set
      if (!session.tabInstanceId) {
        session.tabInstanceId = appInstanceId;
      }

      // Apply any additional data
      if (additionalData) {
        Object.assign(session, additionalData);
      }

      await db.put(DATABASE_STORES.RECORDING_SESSIONS, session);
    }
  } catch (error) {
    if (isLocalEnv) {
      console.error("Failed to update recording session state:", error);
    } else {
      datadogLogs.logger.error(
        "Ambient Scribe",
        {
          message: "Failed to update recording session state",
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
                }
              : error
        },
        error instanceof Error ? error : undefined
      );
    }
  }
};

/**
 * Restore a recording session after page reload
 * Updates Redux state and broadcasts to other tabs
 */
export const restoreRecordingSession = async (
  session: RecordingSession,
  dispatch: any,
  audioWorkerRef: React.MutableRefObject<any>,
  currentUser: any
) => {
  try {
    if (isLocalEnv) {
      console.log("Restoring recording session infrastructure for:", session);
    } else {
      datadogLogs.logger.log("Ambient Scribe", {
        message: "Restoring recording session infrastructure",
        recordingId: session.recordingId,
        patientId: session.patientId
      });
    }

    // Update the session with the new tabInstanceId (this tab now owns it after reload)
    await saveRecordingSession({
      ...session,
      tabInstanceId: appInstanceId,
      recordingState: RECORDING_STATE_PAUSED
    });

    // Restore Redux state to show recording widget
    dispatch(
      startScribeRecording({
        patientId: session.patientId,
        chartNoteId: session.chartNoteId
      })
    );

    // Set to paused state since we can't resume actual MediaRecorder without user action
    dispatch(pauseScribeRecording());

    // Broadcast to other tabs that this tab has an active (paused) recording
    dispatchTabSyncMessage({
      action: TAB_SYNC_ACTIONS.PAUSE_SCRIBE_RECORDING_ON_TAB,
      emitterId: appInstanceId
    });

    if (isLocalEnv) {
      console.log(
        `Recording session restored - previous recording ${session.recordingId} will be auto-saved when resuming`
      );
    } else {
      datadogLogs.logger.log("Ambient Scribe", {
        message: "Recording session restored",
        recordingId: session.recordingId,
        note: "Previous recording will be auto-saved when resuming"
      });
    }
  } catch (error) {
    if (isLocalEnv) {
      console.error(
        "Failed to restore recording session infrastructure:",
        error
      );
    } else {
      datadogLogs.logger.error(
        "Ambient Scribe",
        {
          message: "Failed to restore recording session infrastructure",
          error:
            error instanceof Error
              ? {
                  name: error.name,
                  message: error.message,
                  stack: error.stack
                }
              : error
        },
        error instanceof Error ? error : undefined
      );
    }
    // If restoration fails, clear the persisted session
    await clearRecordingSession();
  }
};
